import { useMemo, useState } from "react";
import { Box, ListItemText, Stack, Typography } from "@mui/material";
import { createFilterOptions } from "@mui/material/Autocomplete";
import { useTranslation } from "react-i18next";
import { AppAutocomplete, AppButton, AppTextField } from "../form";
import { FORMULA_TOKEN_GROUPS, columnFormulaChips } from "../../utils/formulaTokenGroups";
import type { TableColumnMapping } from "../../utils/tableMappingBuilder";
import { useAppCustomStyles } from "../../assets/theme/custom/hooks";

type TokenOption = {
  token: string;
  label: string;
};

type CategoryOption = {
  id: string;
  label: string;
  tokens: TokenOption[];
};

type Props = {
  columns: TableColumnMapping[];
  disabled?: boolean;
  onApply: (token: string) => void;
};

const filterCategories = createFilterOptions<CategoryOption>({
  stringify: (option) => option.label,
});

const filterTokens = createFilterOptions<TokenOption>({
  stringify: (option) => `${option.label} ${option.token}`,
});

export default function FormulaTokenPicker({ columns, disabled, onApply }: Props) {
  const { t } = useTranslation();
  const styles = useAppCustomStyles().components.tableColumnsEditor;
  const [categoryId, setCategoryId] = useState(FORMULA_TOKEN_GROUPS[0]?.id ?? "range");
  const [token, setToken] = useState("");

  const categories = useMemo<CategoryOption[]>(() => {
    const groups: CategoryOption[] = FORMULA_TOKEN_GROUPS.map((group) => ({
      id: group.id,
      label: t(group.labelKey),
      tokens: group.tokens.map((item) => ({
        token: item.token,
        label: t(item.labelKey),
      })),
    }));
    columns.forEach((column, colIndex) => {
      const field = column.field?.trim();
      if (!field) return;
      groups.push({
        id: `column:${colIndex}:${field}`,
        label: column.header?.trim() || field,
        tokens: columnFormulaChips(field).map((item) => ({
          token: item.token,
          label: item.labelKey.startsWith("visualEditor.")
            ? `${t(item.labelKey)} ${field}`
            : item.labelKey,
        })),
      });
    });
    return groups;
  }, [columns, t]);

  const category = categories.find((item) => item.id === categoryId) ?? categories[0] ?? null;
  const selectedToken = category?.tokens.find((item) => item.token === token) ?? null;

  return (
    <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={styles.formulaPicker}>
      <AppAutocomplete<CategoryOption>
        label={t("visualEditor.formulaCategory")}
        options={categories}
        value={category}
        disabled={disabled || categories.length === 0}
        disableClearable
        autoHighlight
        openOnFocus
        filterOptions={filterCategories}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        onChange={(_, option) => {
          if (!option) return;
          setCategoryId(option.id);
          setToken("");
        }}
        sx={styles.formulaPickerCategory}
        slotProps={{
          listbox: { sx: styles.formulaPickerListbox },
          popper: { placement: "bottom-start", sx: styles.formulaPickerPopper },
        }}
        renderInput={(params) => (
          <AppTextField {...params} label={t("visualEditor.formulaCategory")} margin="none" size="small" />
        )}
      />
      <AppAutocomplete<TokenOption>
        label={t("visualEditor.formulaToken")}
        options={category?.tokens ?? []}
        value={selectedToken}
        disabled={disabled || !category}
        autoHighlight
        openOnFocus
        filterOptions={filterTokens}
        getOptionLabel={(option) => `${option.label}  ${option.token}`}
        isOptionEqualToValue={(a, b) => a.token === b.token}
        onChange={(_, option) => setToken(option?.token ?? "")}
        sx={styles.formulaPickerToken}
        slotProps={{
          listbox: { sx: styles.formulaPickerListbox },
          popper: { placement: "bottom-start", sx: styles.formulaPickerPopper },
        }}
        renderOption={(props, option) => {
          const { key, ...optionProps } = props;
          return (
            <Box component="li" key={key} {...optionProps} sx={styles.formulaPickerOption}>
              <ListItemText
                primary={option.label}
                secondary={
                  <Typography variant="caption" color="text.secondary" component="span">
                    {option.token}
                  </Typography>
                }
              />
            </Box>
          );
        }}
        renderInput={(params) => (
          <AppTextField
            {...params}
            label={t("visualEditor.formulaToken")}
            placeholder={t("visualEditor.formulaTokenPlaceholder")}
            margin="none"
            size="small"
          />
        )}
      />
      <AppButton
        variant="outlined"
        disabled={disabled || !selectedToken}
        onClick={() => {
          if (!selectedToken) return;
          onApply(selectedToken.token);
        }}
        sx={styles.formulaPickerApply}
      >
        {t("visualEditor.formulaApply")}
      </AppButton>
    </Stack>
  );
}
